import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"

import { BOOKING_STATUSES } from "@/lib/domain"
import { PRO_SHOP_PAYMENT_METHODS } from "@/lib/pro-shop"

const optionalText = z.string().trim().max(120).optional()
const courseSlug = z.string().trim().min(1).max(80)
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Choose a valid operating date")
const teeTimeFields = {
  startMinutes: z.number().int().min(0).max(1439),
  holes: z.union([z.literal(9), z.literal(18)]),
  capacity: z.number().int().min(1).max(12),
  basePriceCents: z.number().int().min(0).max(100_000_000),
  status: z.enum(["open", "blocked"]),
  notes: z.string().trim().max(240).optional(),
}

async function runDemoMutation<T>(kind: string, action: () => Promise<T>) {
  const [{ env }, resetLock] = await Promise.all([
    import("cloudflare:workers"),
    import("@/db/reset-lock.server"),
  ])
  if (!resetLock.isDemoResetProtectionEnabled(env)) {
    return action()
  }
  return resetLock.runWithDemoWriteLease(env.DB, kind, action)
}

export const getViewerFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getCurrentUser } = await import("@/db/auth.server")
    return getCurrentUser()
  }
)

export const getDemoResetStatusFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const [{ env }, reset] = await Promise.all([
      import("cloudflare:workers"),
      import("@/db/demo-reset.server"),
    ])

    if (env.DEMO_RESET_ENABLED !== "true") {
      return reset.disabledPublicDemoResetStatus()
    }

    try {
      return await reset.getPublicDemoResetStatus(env.DB)
    } catch {
      return reset.unavailablePublicDemoResetStatus()
    }
  }
)

export const getDemoPageDataFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const [{ getCurrentUser }, { getDemoAccounts }] = await Promise.all([
      import("@/db/auth.server"),
      import("@/db/data.server"),
    ])
    const [viewer, accounts] = await Promise.all([
      getCurrentUser(),
      getDemoAccounts(),
    ])

    return { viewer, accounts }
  }
)

export const signInDemoFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      userId: z.enum([
        "user-demo-golfer",
        "user-demo-staff",
        "user-demo-manager",
      ]),
    })
  )
  .handler(async ({ data }) => {
    return runDemoMutation("auth:demo-sign-in", async () => {
      const { signInAsDemoUser } = await import("@/db/auth.server")
      const viewer = await signInAsDemoUser(data.userId)

      if (!viewer) {
        throw new Error("Demo account not found")
      }

      return viewer
    })
  })

export const signInFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      email: z.string().trim().toLowerCase().email().max(254),
      password: z.string().min(8).max(128),
    })
  )
  .handler(async ({ data }) => {
    return runDemoMutation("auth:credential-sign-in", async () => {
      const { signInWithDemoCredentials } = await import("@/db/auth.server")
      const viewer = await signInWithDemoCredentials(data.email, data.password)

      if (!viewer) {
        throw new Error("INVALID_CREDENTIALS")
      }

      return viewer
    })
  })

export const signOutDemoFn = createServerFn({ method: "POST" }).handler(
  async () => {
    const { signOutDemoUser } = await import("@/db/auth.server")
    await signOutDemoUser()
    return { ok: true as const }
  }
)

export const getAvailabilityFn = createServerFn({ method: "GET" })
  .validator(
    z.object({
      courseSlug: optionalText,
      date: optionalText,
    })
  )
  .handler(async ({ data }) => {
    const { getAvailability } = await import("@/db/data.server")
    return getAvailability(data)
  })

export const getTeeTimeFn = createServerFn({ method: "GET" })
  .validator(z.object({ teeTimeId: z.string().min(1).max(160) }))
  .handler(async ({ data }) => {
    const { getTeeTimeDetail } = await import("@/db/data.server")
    return getTeeTimeDetail(data.teeTimeId)
  })

export const startBookingCheckoutFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      teeTimeId: z.string().min(1).max(160),
      playerNames: z.array(z.string().trim().min(1).max(80)).min(1).max(4),
      leadEmail: z.string().trim().toLowerCase().email().max(254),
      cartPreference: z.enum(["walking", "riding"]),
      notes: z.string().trim().max(500).optional(),
    })
  )
  .handler(async ({ data }) => {
    return runDemoMutation("booking:start-checkout", async () => {
      const { startBookingCheckout } = await import("@/db/payments.server")
      return startBookingCheckout(data)
    })
  })

export const syncBookingCheckoutFn = createServerFn({ method: "POST" })
  .validator(z.object({ bookingId: z.string().min(1).max(120) }))
  .handler(async ({ data }) => {
    return runDemoMutation("booking:sync-checkout", async () => {
      const { syncBookingCheckout } = await import("@/db/payments.server")
      return syncBookingCheckout(data.bookingId)
    })
  })

export const abandonBookingCheckoutFn = createServerFn({ method: "POST" })
  .validator(z.object({ bookingId: z.string().min(1).max(120) }))
  .handler(async ({ data }) => {
    return runDemoMutation("booking:abandon-checkout", async () => {
      const { abandonBookingCheckout } = await import("@/db/payments.server")
      return abandonBookingCheckout(data.bookingId)
    })
  })

export const getMyBookingsFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getMyBookings } = await import("@/db/data.server")
    return getMyBookings()
  }
)

export const getMyBookingFn = createServerFn({ method: "GET" })
  .validator(z.object({ bookingId: z.string().min(1).max(120) }))
  .handler(async ({ data }) => {
    const { getMyBooking } = await import("@/db/data.server")
    return getMyBooking(data.bookingId)
  })

export const cancelMyBookingFn = createServerFn({ method: "POST" })
  .validator(z.object({ bookingId: z.string().min(1).max(120) }))
  .handler(async ({ data }) => {
    return runDemoMutation("booking:cancel", async () => {
      const { cancelMyBooking } = await import("@/db/data.server")

      try {
        return await cancelMyBooking(data.bookingId)
      } catch (error) {
        if (error instanceof Error && error.message === "AUTH_REQUIRED") {
          return { ok: false as const }
        }
        throw error
      }
    })
  })

export const getStaffOverviewFn = createServerFn({ method: "GET" })
  .validator(z.object({ date: optionalText }))
  .handler(async ({ data }) => {
    const { getStaffOverview } = await import("@/db/data.server")
    return getStaffOverview(data.date)
  })

export const getStaffTeeSheetFn = createServerFn({ method: "GET" })
  .validator(
    z.object({
      courseSlug: optionalText,
      date: optionalText,
    })
  )
  .handler(async ({ data }) => {
    const { getStaffTeeSheet } = await import("@/db/data.server")
    return getStaffTeeSheet(data)
  })

export const fillTeeTimeDayFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      courseSlug,
      date: isoDate,
    })
  )
  .handler(async ({ data }) => {
    return runDemoMutation("tee-time:fill-day", async () => {
      const { fillTeeTimeDay } = await import("@/db/data.server")
      return fillTeeTimeDay(data)
    })
  })

export const createTeeTimeFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      courseSlug,
      date: isoDate,
      ...teeTimeFields,
    })
  )
  .handler(async ({ data }) => {
    return runDemoMutation("tee-time:create", async () => {
      const { createTeeTime } = await import("@/db/data.server")
      return createTeeTime(data)
    })
  })

export const updateTeeTimeFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      teeTimeId: z.string().min(1).max(160),
      ...teeTimeFields,
    })
  )
  .handler(async ({ data }) => {
    return runDemoMutation("tee-time:update", async () => {
      const { updateTeeTime } = await import("@/db/data.server")
      return updateTeeTime(data)
    })
  })

export const deleteTeeTimeFn = createServerFn({ method: "POST" })
  .validator(z.object({ teeTimeId: z.string().min(1).max(160) }))
  .handler(async ({ data }) => {
    return runDemoMutation("tee-time:delete", async () => {
      const { deleteTeeTime } = await import("@/db/data.server")
      return deleteTeeTime(data.teeTimeId)
    })
  })

export const getStaffBookingsFn = createServerFn({ method: "GET" })
  .validator(
    z.object({
      query: optionalText,
      status: optionalText,
    })
  )
  .handler(async ({ data }) => {
    const { getStaffBookings } = await import("@/db/data.server")
    return getStaffBookings(data)
  })

export const getStaffCustomersFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getStaffCustomers } = await import("@/db/data.server")
    return getStaffCustomers()
  }
)

export const getMembershipFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getMembership } = await import("@/db/payments.server")
    return getMembership()
  }
)

export const startMembershipCheckoutFn = createServerFn({
  method: "POST",
}).handler(async () => {
  return runDemoMutation("membership:start-checkout", async () => {
    const { startMembershipCheckout } = await import("@/db/payments.server")
    return startMembershipCheckout()
  })
})

export const syncMembershipCheckoutFn = createServerFn({
  method: "POST",
}).handler(async () => {
  return runDemoMutation("membership:sync-checkout", async () => {
    const { syncMembershipCheckout } = await import("@/db/payments.server")
    return syncMembershipCheckout()
  })
})

export const abandonMembershipCheckoutFn = createServerFn({
  method: "POST",
}).handler(async () => {
  return runDemoMutation("membership:abandon-checkout", async () => {
    const { abandonMembershipCheckout } = await import("@/db/payments.server")
    return abandonMembershipCheckout()
  })
})

export const getStaffPaymentsFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getStaffPayments } = await import("@/db/payments.server")
    return getStaffPayments()
  }
)

export const getStaffCourseDataFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getStaffCourseData } = await import("@/db/data.server")
    return getStaffCourseData()
  }
)

export const getStaffProShopFn = createServerFn({ method: "GET" })
  .validator(
    z.object({
      date: optionalText,
      page: z.number().int().min(1).max(10_000).optional(),
    })
  )
  .handler(async ({ data }) => {
    const { getStaffProShop } = await import("@/db/data.server")
    return getStaffProShop(data.date, data.page)
  })

export const checkoutProShopFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      idempotencyKey: z.string().uuid(),
      bookingId: z.string().min(1).max(120),
      paymentMethod: z.enum(PRO_SHOP_PAYMENT_METHODS),
      items: z
        .array(
          z.object({
            itemId: z.string().min(1).max(180),
            quantity: z.number().int().min(1).max(20),
          })
        )
        .min(1)
        .max(40),
    })
  )
  .handler(async ({ data }) => {
    return runDemoMutation("pro-shop:checkout", async () => {
      const { checkoutProShop } = await import("@/db/data.server")
      return checkoutProShop(data)
    })
  })

export const getStaffShellDataFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getStaffShellData } = await import("@/db/data.server")
    return getStaffShellData()
  }
)

export const updateBookingStatusFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      bookingId: z.string().min(1).max(120),
      status: z.enum(BOOKING_STATUSES),
    })
  )
  .handler(async ({ data }) => {
    return runDemoMutation("booking:update-status", async () => {
      const { updateBookingStatus } = await import("@/db/data.server")
      return updateBookingStatus(data.bookingId, data.status)
    })
  })

export const updateTeeTimeStatusFn = createServerFn({ method: "POST" })
  .validator(
    z.object({
      teeTimeId: z.string().min(1).max(160),
      status: z.enum(["open", "blocked"]),
    })
  )
  .handler(async ({ data }) => {
    return runDemoMutation("tee-time:update-status", async () => {
      const { updateTeeTimeStatus } = await import("@/db/data.server")
      return updateTeeTimeStatus(data.teeTimeId, data.status)
    })
  })
